import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { CheckCircle, AlertTriangle, AlertCircle, Mail } from 'lucide-react';
// Reemplaza esto con tu URL de Apps Script desplegada
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbwpKaTIQXCeRV0chDA2q0wG989223t2pW-3HiTp_QH7ZZk8E0UaGdqxu6KE9HXW5meJ/exec';
type Question = {
  id: number;
  text: string;
};
const QUESTIONS: Question[] = [{
  id: 1,
  text: '¿Sientes que tu corazón se acelera sin razón aparente?'
}, {
  id: 2,
  text: '¿Te cuesta conciliar el sueño por pensamientos repetitivos?'
}, {
  id: 3,
  text: '¿Experimentas tensión muscular en cuello, mandíbula o espalda?'
}, {
  id: 4,
  text: '¿Sientes una presión en el pecho o dificultad para respirar?'
}, {
  id: 5,
  text: '¿Te resulta difícil concentrarte en tareas cotidianas?'
}, {
  id: 6,
  text: '¿Evitas situaciones sociales por miedo o incomodidad?'
}, {
  id: 7,
  text: '¿Sientes que algo malo va a pasar sin motivo concreto?'
}, {
  id: 8,
  text: '¿Tienes episodios de sudoración o temblores inesperados?'
}, {
  id: 9,
  text: '¿Te sientes irritable o al borde del llanto sin causa clara?'
}, {
  id: 10,
  text: '¿Experimentas mareos o sensación de irrealidad?'
}, {
  id: 11,
  text: '¿Revisas compulsivamente cosas (celular, puertas, etc.)?'
}, {
  id: 12,
  text: '¿Sientes que pierdes el control de tu cuerpo o mente?'
}];
const OPTIONS = [{
  label: 'Nunca',
  score: 0
}, {
  label: 'A veces',
  score: 1
}, {
  label: 'Frecuentemente',
  score: 2
}, {
  label: 'Siempre',
  score: 3
}];
type Answer = {
  questionId: number;
  score: number;
};
export function AnxietyTest() {
  const [step, setStep] = useState<'intro' | 'questions' | 'email' | 'result'>('intro');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleAnswer = (score: number) => {
    const newAnswers = [...answers, {
      questionId: QUESTIONS[currentQuestionIndex].id,
      score
    }];
    setAnswers(newAnswers);
    if (currentQuestionIndex < QUESTIONS.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setStep('email');
    }
  };
  const calculateResults = () => {
    const totalScore = answers.reduce((acc, curr) => acc + curr.score, 0);
    let level: 'Leve' | 'Moderada' | 'Alta' = 'Leve';
    let message = '';
    let color = '';
    if (totalScore <= 12) {
      level = 'Leve';
      color = 'text-green-600';
      message = 'Tu nivel de ansiedad es manejable, pero Ansiedad Exprés puede ayudarte a mantener el control en esos momentos donde sientes que se escala. Tener un protocolo listo es la mejor prevención.';
    } else if (totalScore <= 24) {
      level = 'Moderada';
      color = 'text-yellow-600';
      message = 'Tu ansiedad está afectando tu día a día. Ansiedad Exprés te da un protocolo de 5 minutos para frenar esos episodios antes de que te desborden. Miles de personas ya lo están usando.';
    } else {
      level = 'Alta';
      color = 'text-red-600';
      message = 'Tu nivel de ansiedad es significativo y merece atención inmediata. Ansiedad Exprés te da herramientas concretas para recuperar el control en minutos. No tienes que seguir sintiéndote así.';
    }
    return {
      totalScore,
      level,
      message,
      color
    };
  };
  const handleSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;
    setIsSubmitting(true);
    const results = calculateResults();
    try {
      if (GOOGLE_SCRIPT_URL !== 'GOOGLE_SCRIPT_URL_PLACEHOLDER') {
        await fetch(GOOGLE_SCRIPT_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email,
            answers,
            totalScore: results.totalScore,
            level: results.level,
            timestamp: new Date().toISOString()
          })
        });
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
      setStep('result');
    } catch (error) {
      console.error('Error submitting test:', error);
      setStep('result');
    } finally {
      setIsSubmitting(false);
    }
  };
  const results = calculateResults();
  return <section id="anxiety-test" className="py-20 px-4 relative overflow-hidden" data-id="element-1">
      <div className="max-w-3xl mx-auto relative z-10" data-id="element-2">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white border border-[#E8E8E6] rounded-3xl p-8 md:p-12 shadow-sm relative overflow-hidden" data-id="element-3">
          <AnimatePresence mode="wait" data-id="element-4">
            {step === 'intro' && <motion.div key="intro" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="text-center" data-id="element-5">
                <h2 className="text-3xl md:text-4xl font-semibold text-[#3A3A3A] mb-6" data-id="element-6">
                  Descubre tu Nivel de Ansiedad
                </h2>
                <p className="text-lg text-[#7A7A7A] mb-8 max-w-xl mx-auto font-light" data-id="element-7">
                  Responde este breve test de 12 preguntas para entender mejor
                  lo que estás sintiendo y recibir una recomendación
                  personalizada.
                </p>
                <Button size="lg" onClick={() => setStep('questions')} data-id="element-8">
                  Comenzar Test
                </Button>
                <p className="text-xs text-[#7A7A7A] mt-4" data-id="element-9">
                  Toma menos de 2 minutos. Totalmente confidencial.
                </p>
              </motion.div>}

            {step === 'questions' && <motion.div key="questions" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="w-full" data-id="element-10">
                <div className="mb-8" data-id="element-11">
                  <div className="flex justify-between text-xs text-[#7A7A7A] mb-2" data-id="element-12">
                    <span data-id="element-13">
                      Pregunta {currentQuestionIndex + 1} de {QUESTIONS.length}
                    </span>
                    <span data-id="element-14">
                      {Math.round(currentQuestionIndex / QUESTIONS.length * 100)}
                      % completado
                    </span>
                  </div>
                  <div className="h-2 bg-[#F7F7F5] rounded-full overflow-hidden" data-id="element-15">
                    <motion.div className="h-full bg-[#5BBFB5]" initial={{
                  width: 0
                }} animate={{
                  width: `${currentQuestionIndex / QUESTIONS.length * 100}%`
                }} transition={{
                  duration: 0.3
                }} data-id="element-16" />
                  </div>
                </div>

                <h3 className="text-2xl font-semibold text-[#3A3A3A] mb-8 text-center min-h-[80px] flex items-center justify-center" data-id="element-17">
                  {QUESTIONS[currentQuestionIndex].text}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-18">
                  {OPTIONS.map(option => <button key={option.label} onClick={() => handleAnswer(option.score)} className="p-4 rounded-xl border border-[#E8E8E6] bg-white hover:bg-[#5BBFB5] hover:text-white hover:border-[#5BBFB5] transition-all duration-200 text-lg font-medium text-[#3A3A3A] text-left" data-id="element-19">
                      {option.label}
                    </button>)}
                </div>
              </motion.div>}

            {step === 'email' && <motion.div key="email" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="text-center max-w-md mx-auto" data-id="element-20">
                <div className="w-16 h-16 bg-[#5BBFB5]/10 rounded-full flex items-center justify-center mx-auto mb-6 text-[#5BBFB5]" data-id="element-21">
                  <Mail className="w-8 h-8" data-id="element-22" />
                </div>
                <h3 className="text-2xl font-semibold text-[#3A3A3A] mb-4" data-id="element-23">
                  ¡Test Completado!
                </h3>
                <p className="text-[#7A7A7A] mb-8 font-light" data-id="element-24">
                  Ingresa tu correo electrónico para ver tu resultado detallado
                  y recibir tu recomendación.
                </p>

                <form onSubmit={handleSubmitEmail} className="space-y-4" data-id="element-25">
                  <input type="email" required placeholder="tu@correo.com" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-6 py-4 rounded-xl border border-[#E8E8E6] focus:border-[#5BBFB5] focus:ring-2 focus:ring-[#5BBFB5]/20 outline-none transition-all bg-[#F7F7F5]" data-id="element-26" />
                  <Button type="submit" size="lg" className="w-full" disabled={isSubmitting} data-id="element-27">
                    {isSubmitting ? 'Procesando...' : 'Ver Mis Resultados'}
                  </Button>
                </form>
                <p className="text-xs text-[#7A7A7A] mt-4" data-id="element-28">
                  No compartiremos tu correo con terceros.
                </p>
              </motion.div>}

            {step === 'result' && <motion.div key="result" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="text-center" data-id="element-29">
                <div className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#F7F7F5]" data-id="element-30">
                  {results.level === 'Leve' && <CheckCircle className="w-10 h-10 text-green-500" data-id="element-31" />}
                  {results.level === 'Moderada' && <AlertCircle className="w-10 h-10 text-yellow-500" data-id="element-32" />}
                  {results.level === 'Alta' && <AlertTriangle className="w-10 h-10 text-red-500" data-id="element-33" />}
                </div>

                <h3 className="text-xl text-[#7A7A7A] mb-2 font-light" data-id="element-34">
                  Tu resultado indica:
                </h3>
                <h2 className={`text-4xl font-semibold mb-6 ${results.color}`} data-id="element-35">
                  Ansiedad {results.level}
                </h2>

                <div className="bg-[#F7F7F5] rounded-2xl p-6 mb-8 border border-[#E8E8E6]" data-id="element-36">
                  <p className="text-lg text-[#3A3A3A] leading-relaxed font-light" data-id="element-37">
                    {results.message}
                  </p>
                </div>

                <div className="space-y-4" data-id="element-38">
                  <a href="https://pay.hotmart.com/D104310882V?bid=1770407336992" target="_blank" rel="noopener noreferrer" className="block w-full" data-id="element-39">
                    <Button size="lg" className="w-full py-6 text-lg" withIcon data-id="element-40">
                      <span className="flex items-center gap-2 flex-wrap justify-center" data-id="element-41">
                        Obtener Ansiedad Exprés —
                        <span className="line-through text-white/60 decoration-1" data-id="element-42">
                          $29.99
                        </span>
                        <span className="font-bold" data-id="element-43">$7.99 USD</span>
                      </span>
                    </Button>
                  </a>
                  <p className="text-xs text-[#7A7A7A]" data-id="element-44">
                    *Este test es orientativo y no sustituye un diagnóstico
                    profesional.
                  </p>
                </div>
              </motion.div>}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>;
}